import { TextField } from '@material-ui/core';
import { Autocomplete, AutocompleteChangeReason, IconButton } from '@mui/material';
import { SyntheticEvent, useEffect, useState, useRef, useLayoutEffect } from 'react';
import { MessageRecipients, RecipientTreeOption } from '../../models/communication.types';
import CloseIcon from '@material-ui/icons/Close';
import { CheckboxOne } from '../CheckboxOne/CheckboxOne.component';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';

interface RecipientsAutocompleteProps {
  recipientsStructure?: MessageRecipients;
  selectedRecipients: RecipientTreeOption[];
  onChange: (options: RecipientTreeOption[]) => void;
  isCZE: boolean;
  isCZWO: boolean;
}

const ChipProps = {
  deleteIcon: <CloseIcon />,
  style: { color: '#2C2D3E', background: '#FAFAFF', border: '1px solid rgba(44, 45, 62, 0.3' },
};

export const RecipientsAutocomplete: React.FC<RecipientsAutocompleteProps> = (props) => {
  const [recipientTreeOptions, setRecipientTreeOptions] = useState<RecipientTreeOption[]>([]);
  const [allSubordinatesChecked, setAllSubordinatesChecked] = useState<boolean>(false);
  const [totalNumberOfSubordinates, setTotalNumberOfSubordinates] = useState<number>(0);
  const inputRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (!inputRef.current) return;

    const observer = new ResizeObserver(() => {
      setWidth(inputRef.current!.offsetWidth);
    });

    observer.observe(inputRef.current);
    return () => observer.disconnect();
  }, []);

  const allSubordinatesTreeOption = {
    displayName: 'Zaznacz wszystkich podległych',
    userId: '',
    functionId: '',
    function: -1,
    firstName: '',
    lastName: '',
  };

  const supervisorDefault: RecipientTreeOption[] = [];
  if (props.recipientsStructure !== undefined) {
    props.recipientsStructure.supervisor.forEach((team, index, array) => {
      supervisorDefault.push({
        userId: team.userId,
        functionId: team.firstName !== null ? team.functionId : '',
        function: team.function,
        firstName: team.firstName,
        lastName: team.lastName,
        displayName: team.firstName !== null ? team.displayName : '',
        depth: 0,
        canCollapse: false,
        hidden: team.firstName === null,
        hasNeighbour: false,
      });
    });
  }

  const buildRecipientsTree = () => {
    if (props.recipientsStructure !== undefined) {
      const flatRecipients: RecipientTreeOption[] = [];
      flatRecipients.push(allSubordinatesTreeOption);
      let subordinatesCounter = 0;
      const depth = 0;
      props.recipientsStructure.team.forEach((team, index, array) => {
        subordinatesCounter++;
        flatRecipients.push({
          userId: team.userId,
          functionId: team.functionId,
          function: team.function,
          firstName: team.firstName,
          lastName: team.lastName,
          displayName: team.displayName,
          depth: depth,
          canCollapse: team.inferiors !== undefined && team.inferiors.length > 0 ? true : undefined,
          hidden: false,
          hasNeighbour: array.length > 1 && index < array.length - 1,
        });
        if (team.inferiors !== undefined) {
          team.inferiors.forEach((inferior, index, array) => {
            subordinatesCounter++;
            flatRecipients.push({
              userId: inferior.userId,
              functionId: inferior.functionId,
              function: inferior.function,
              firstName: inferior.firstName,
              lastName: inferior.lastName,
              displayName: inferior.displayName,
              depth: depth + 1,
              parentIds: [team.userId],
              canCollapse: inferior.inferiors !== undefined && inferior.inferiors.length > 0 ? false : undefined, // intentionally collapse last nesting level
              hidden: false,
              hasNeighbour: array.length > 1 && index < array.length - 1,
            });
            if (inferior.inferiors !== undefined) {
              inferior.inferiors.forEach((superInferior, index, array) => {
                subordinatesCounter++;
                flatRecipients.push({
                  userId: superInferior.userId,
                  functionId: superInferior.functionId,
                  function: superInferior.function,
                  firstName: superInferior.firstName,
                  lastName: superInferior.lastName,
                  displayName: superInferior.displayName,
                  depth: depth + 2,
                  parentIds: [team.userId, inferior.userId],
                  hidden: inferior.firstName === null ? false : true, // intentionally hide last nesting level /
                  hasNeighbour: array.length > 1 && index < array.length - 1,
                });
              });
            }
          });
        }
      });
      flatRecipients[flatRecipients.length - 1].isLastSubordinate = true;
      flatRecipients.push(...props.recipientsStructure.peers);
      flatRecipients.unshift(...props.recipientsStructure.supervisor);
      setRecipientTreeOptions(flatRecipients);
      setTotalNumberOfSubordinates(subordinatesCounter);
      if (props.isCZWO || props.isCZE) {
        props.onChange(
          flatRecipients.filter((recipient) => {
            if (recipient.firstName !== null) {
              return recipient.displayName !== allSubordinatesTreeOption.displayName;
            } else {
              return recipient.displayName === allSubordinatesTreeOption.displayName;
            }
          }),
        );
      }
    }
  };

  useEffect(() => {
    buildRecipientsTree();
  }, [props.recipientsStructure]);

  const changeSubordinatesVisibility = (parentId: string, collapse: boolean) => {
    setRecipientTreeOptions((recipientTree) => {
      recipientTree
        .filter((item) => item.parentIds?.includes(parentId))
        .forEach((item) => {
          item.hidden = collapse;
        });
      const parentIndex = recipientTree.findIndex((item) => item.userId === parentId);
      recipientTree[parentIndex].canCollapse = !recipientTree[parentIndex].canCollapse;
      return [...recipientTree];
    });
  };

  const getPeers = (dataSource: RecipientTreeOption[]): RecipientTreeOption[] => {
    return dataSource.filter(
      (recipient) => recipient.displayName !== allSubordinatesTreeOption.displayName && recipient.depth === undefined,
    );
  };

  const getAllSubordinates = (dataSource: RecipientTreeOption[]) => {
    return dataSource.filter(
      (recipient) =>
        recipient.displayName !== allSubordinatesTreeOption.displayName &&
        recipient.depth !== undefined &&
        recipient.firstName !== null,
    );
  };

  const handleRecipientsChange = (
    event: SyntheticEvent<Element, Event>,
    value: RecipientTreeOption[],
    reason: AutocompleteChangeReason,
  ) => {
    if (value.find((value) => value.displayName === allSubordinatesTreeOption.displayName)) {
      let newSubordinates: RecipientTreeOption[] = [];
      if (allSubordinatesChecked) {
        newSubordinates = getPeers(props.selectedRecipients); //get already selected peers
      } else {
        newSubordinates = getAllSubordinates(recipientTreeOptions); //get all possible subordinates
        const selectedPeers = getPeers(props.selectedRecipients);
        newSubordinates.push(...selectedPeers);
      }
      setAllSubordinatesChecked(!allSubordinatesChecked);
      props.onChange(newSubordinates);
    } else props.onChange(value);
    if (reason === 'clear') {
      setAllSubordinatesChecked(false);
    }
  };

  const selectOptionAndChildren = (parentId: string, check: boolean) => {
    const options = recipientTreeOptions.filter(
      (item) => item.userId === parentId || item.parentIds?.includes(parentId),
    );
    if (options)
      if (check) {
        const recipientsSet = new Set<RecipientTreeOption>();
        props.selectedRecipients.forEach((recipient) => recipientsSet.add(recipient));
        options.forEach((newRecipient) =>
          newRecipient.firstName !== null ? recipientsSet.add(newRecipient) : undefined,
        );
        const newRecipientsArray: RecipientTreeOption[] = [];
        recipientsSet.forEach((element) => newRecipientsArray.push(element));
        props.onChange(newRecipientsArray);
      } else {
        options.forEach((option) => {
          const index = props.selectedRecipients.findIndex((recipient) => recipient.userId === option.userId);
          props.selectedRecipients.splice(index, 1);
        });
        props.onChange([...props.selectedRecipients]);
      }
  };

  useEffect(() => {
    if (props.selectedRecipients.length === 0) {
      setAllSubordinatesChecked(false);
    }
  }, [props.selectedRecipients]);

  const hasNoSubordinates = totalNumberOfSubordinates === 0;

  return (
    <Autocomplete
      ref={inputRef}
      limitTags={4}
      multiple
      disableCloseOnSelect
      renderInput={(params) => <TextField {...params} variant="outlined" label="Wybierz z listy" />}
      options={recipientTreeOptions}
      value={props.selectedRecipients}
      getOptionLabel={(option) => {
        const cleanName = option.displayName
          .replace(/\s*\(\d+\)\s*$/, '') // Remove numbers in parentheses at the end
          .replace(/\s*\([^)]*@[^)]*\)\s*$/, '') // Remove email addresses in parentheses at the end
          .trim();
        return cleanName;
      }}
      noOptionsText={'Brak adresatów'}
      getOptionDisabled={(option) =>
        option.firstName === null || (option.displayName === allSubordinatesTreeOption.displayName && hasNoSubordinates)
      }
      renderOption={(renderOptionProps, option, { inputValue, selected }) => (
        <>
          {(inputValue === '' && option.hidden) ||
          (inputValue !== '' && option.displayName === allSubordinatesTreeOption.displayName) ? (
            <></>
          ) : (
            <li
              {...renderOptionProps}
              style={{
                height: width < 600 ? 45 : 35,
                display:
                  (props.isCZE || props.isCZWO) && option.displayName === allSubordinatesTreeOption.displayName
                    ? 'none'
                    : '',
                paddingLeft: inputValue === '' && option.depth !== undefined ? option.depth * 30 + 15 : 0,
                borderBottom:
                  inputValue === '' && option.isLastSubordinate ? '1px solid rgba(44, 45, 62, 0.3)' : undefined,
                borderTop:
                  option.displayName === allSubordinatesTreeOption.displayName
                    ? '1px solid rgba(44, 45, 62, 0.3)'
                    : undefined,
              }}
            >
              {inputValue === '' && option.depth !== undefined && (
                <div style={{ position: 'relative', right: '-6px' }}>
                  <div
                    style={{
                      height: '16px',
                      width: '12px',
                      borderLeft: option.isFirst ? undefined : '1px solid rgba(44, 45, 62, 0.3',
                      borderBottom: '1px solid rgba(44, 45, 62, 0.3',
                    }}
                  />
                  <div
                    style={{
                      height: '16px',
                      width: '12px',
                      borderLeft: option.hasNeighbour ? '1px solid rgba(44, 45, 62, 0.3' : undefined,
                    }}
                  />
                </div>
              )}
              <CheckboxOne
                checked={
                  option.displayName === allSubordinatesTreeOption.displayName ? allSubordinatesChecked : selected
                }
                indeterminate={
                  option.displayName === allSubordinatesTreeOption.displayName && allSubordinatesChecked
                    ? props.selectedRecipients.length < totalNumberOfSubordinates
                    : undefined
                }
                fontSize={28}
                onClick={(e) => {
                  if (option.canCollapse !== undefined) {
                    e.preventDefault();
                    e.stopPropagation();
                    selectOptionAndChildren(option.userId, !selected);
                  }
                }}
              />
              {option.displayName}
              {inputValue === '' && option.canCollapse !== undefined && (
                <IconButton
                  disableRipple
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    changeSubordinatesVisibility(option.userId, option.canCollapse ?? false);
                  }}
                >
                  {option.canCollapse ? <ArrowDropUpOutlinedIcon /> : <ArrowDropDownOutlinedIcon />}
                </IconButton>
              )}
            </li>
          )}
        </>
      )}
      onChange={handleRecipientsChange}
      ChipProps={ChipProps}
    />
  );
};
